import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_USERS } from '../../graphql/queries';
import PropertyManagementFilter from '../properties/PropertyManagementFilter';
import { UserRole } from '../../utils/enums';
import { getPropertyManagerDisplayName } from '../../utils/propertyManagers';

/**
 * Filters Work Order reports by the management assignment on each property.
 * Selecting a person includes properties assigned directly to that manager as
 * well as properties assigned to a management team they belong to.
 */
function WorkOrderPropertyManagementFilter({ value, onChange }) {
  const { data, loading, error } = useQuery(GET_USERS, {
    fetchPolicy: 'cache-first'
  });

  const propertyManagers = useMemo(() => {
    return (data?.users || [])
      .filter((user) => user.role === UserRole.PROPERTY_MANAGER && user.isActive !== false)
      .sort((a, b) => {
        const nameA = getPropertyManagerDisplayName(a) || a.email || '';
        const nameB = getPropertyManagerDisplayName(b) || b.email || '';
        return nameA.localeCompare(nameB);
      });
  }, [data?.users]);

  return (
    <div>
      <label htmlFor="work-order-report-property-management" className="block text-sm font-medium text-gray-700 mb-2">
        Property Manager / Property Manager Team
      </label>
      {loading ? (
        <div className="flex items-center py-2 text-sm text-gray-500">
          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          Loading property managers...
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Failed to load property managers.</p>
      ) : (
        <PropertyManagementFilter
          id="work-order-report-property-management"
          value={value || ''}
          onChange={onChange}
          users={propertyManagers}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      )}
      <p className="mt-1 text-xs text-gray-500">
        Selecting a person includes properties assigned directly to them or to a management team they belong to.
      </p>
    </div>
  );
}

export default WorkOrderPropertyManagementFilter;
